// 公開承認ページ（ドライバーがトークン付きURL `/?token=xxx` でスマホアクセス）
// 通常アプリ（事務所用）とは別レンダリング。StoreProvider に依存しない。

import React, { useEffect, useState } from "react";
import "./styles/driver-approval.css";
import logo from "./assets/hsd-logo.png";
import { ApprovalApp, PrintSheet, NoticeSheet, statementToApprovalView, type ApprovalView } from "./screens/DriverApproval";
import { fetchApproval, submitApprovalAction, type ApprovalStatus } from "./lib/approvalApi";
import type { Statement } from "./types";

export function PublicApproval({ token }: { token: string }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<ApprovalView | null>(null);
  const [status, setStatus] = useState<ApprovalStatus>("viewed");
  const [approvedStamp, setApprovedStamp] = useState<string | undefined>();

  useEffect(() => {
    let alive = true;
    (async () => {
      const r = await fetchApproval(token);
      if (!alive) return;
      if (!r.ok) {
        setError(r.error);
      } else {
        setView(statementToApprovalView(r.snapshot as Statement));
        setStatus(r.status);
        if (r.approvedAt) {
          const dt = new Date(r.approvedAt);
          const p = (n: number) => String(n).padStart(2, "0");
          setApprovedStamp(`${dt.getFullYear()}/${p(dt.getMonth() + 1)}/${p(dt.getDate())} ${p(dt.getHours())}:${p(dt.getMinutes())}`);
        }
      }
      setLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, [token]);

  if (loading) {
    return <CenterMsg>読み込み中...</CenterMsg>;
  }
  if (error || !view) {
    return (
      <CenterMsg>
        <img src={logo} alt="HS-deliverys" style={{ height: 24, marginBottom: 16 }} />
        <div style={{ fontWeight: 700, marginBottom: 6 }}>明細を表示できませんでした</div>
        <div style={{ fontSize: 13, color: "#6b7280" }}>{error || "リンクが無効です"}</div>
        <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 12 }}>
          お手数ですが、事務所までお問い合わせください。
        </div>
      </CenterMsg>
    );
  }

  const onApprove = async (): Promise<boolean> => {
    const r = await submitApprovalAction(token, "approve");
    return r.ok;
  };
  const onQuestion = async (q: string): Promise<boolean> => {
    const r = await submitApprovalAction(token, "question", q);
    return r.ok;
  };

  return <PublicApprovalView view={view} status={status} approvedStamp={approvedStamp} onApprove={onApprove} onQuestion={onQuestion} />;
}

// 印刷モード（支払通知書 / 売上明細表）を切り替えながら window.print() する
function PublicApprovalView({
  view, status, approvedStamp, onApprove, onQuestion,
}: {
  view: ApprovalView;
  status: ApprovalStatus;
  approvedStamp?: string;
  onApprove: () => Promise<boolean>;
  onQuestion: (q: string) => Promise<boolean>;
}) {
  const [printMode, setPrintMode] = useState<"notice" | "sales">("notice");
  const [printTick, setPrintTick] = useState(0);
  useEffect(() => { if (printTick > 0) window.print(); }, [printTick]);
  const doPrint = (mode: "notice" | "sales") => { setPrintMode(mode); setPrintTick((x) => x + 1); };

  return (
    <div className="dapp dapp-public">
      <div className="dapp-stage">
        <div className="dapp-phone">
          <ApprovalApp
            d={view}
            onApprove={onApprove}
            onQuestion={onQuestion}
            onPrint={doPrint}
            initialApproved={status === "approved"}
            approvedStamp={approvedStamp}
          />
        </div>
      </div>
      <div className="dapp-print-root">
        {printMode === "notice" ? <NoticeSheet d={view} /> : <PrintSheet d={view} />}
      </div>
    </div>
  );
}

function CenterMsg({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        padding: 24,
        background: "#eef0f4",
        fontFamily: "'Noto Sans JP', -apple-system, system-ui, sans-serif",
        color: "#1b2536",
      }}
    >
      {children}
    </div>
  );
}
